import { $t } from '../../composables/i18n'
import { Codebook } from '../Codebook'

export enum EmployeeActivityTypeId {
  LOGGED_IN = 'user.logged-in',
  LOGIN_VALID = 'user.login-valid',
  LOGIN_FAILED = 'user.login-failed',
  LOGGED_OUT = 'user.logged-out',
  SIGNED_IN = 'user.signed-in',
  IP_DENIED = 'user.ip-denied',
  TWO_FACTOR_SENT = 'user.two-factor-sent',
  TWO_FACTOR_VERIFIED = 'user.two-factor-verified',
  OTP_SET_UP = 'user.otp-set-up',
  PASSWORD_CHANGED = 'user.password-changed',
  PASSWORD_CHANGE_REQUESTED = 'user.password-change-requested',
  GRACE_LOGIN = 'user.grace-login',
  GRACE_LOGIN_CREATED = 'user.grace-login-created',
  GRACE_LOGIN_REVOKED = 'user.grace-login-revoked',
  EMPLOYEE_INVITED = 'user.employee-invited',
  INVITATION_ACCEPTED = 'user.invitation-accepted',
  EMPLOYEE_CREATED = 'user.employee-created',
  EMPLOYEE_UPDATED = 'user.employee-updated',
  EMPLOYEE_ACTIVATED = 'user.employee-activated',
  EMPLOYEE_DEACTIVATED = 'user.employee-deactivated',
  EMPLOYEE_ROLES_CHANGED = 'user.employee-roles-changed',
  EMPLOYEE_COUNTRIES_CHANGED = 'user.employee-countries-changed',
  API_USER_CREATED = 'user.api-user-created',
  API_USER_UPDATED = 'user.api-user-updated',
  API_USER_ACTIVATED = 'user.api-user-activated',
  API_USER_DEACTIVATED = 'user.api-user-deactivated',
  API_KEY_GENERATED = 'user.api-key-generated',
  API_KEY_REVOKED = 'user.api-key-revoked',
}

export class EmployeeActivityType extends Codebook<EmployeeActivityTypeId> {
  static readonly LOGGED_IN = new EmployeeActivityType(EmployeeActivityTypeId.LOGGED_IN, () =>
    $t('Logged in'),
  )
  static readonly LOGIN_VALID = new EmployeeActivityType(EmployeeActivityTypeId.LOGIN_VALID, () =>
    $t('Credentials accepted. Verification required'),
  )
  static readonly LOGIN_FAILED = new EmployeeActivityType(EmployeeActivityTypeId.LOGIN_FAILED, () =>
    $t('Login failed'),
  )
  static readonly LOGGED_OUT = new EmployeeActivityType(EmployeeActivityTypeId.LOGGED_OUT, () =>
    $t('Logged out'),
  )
  static readonly SIGNED_IN = new EmployeeActivityType(EmployeeActivityTypeId.SIGNED_IN, () =>
    $t('Logged in using external provider'),
  )
  static readonly IP_DENIED = new EmployeeActivityType(EmployeeActivityTypeId.IP_DENIED, () =>
    $t('Login blocked by IP rule'),
  )
  static readonly TWO_FACTOR_SENT = new EmployeeActivityType(
    EmployeeActivityTypeId.TWO_FACTOR_SENT,
    () => $t('Two-factor verification sent'),
  )
  static readonly TWO_FACTOR_VERIFIED = new EmployeeActivityType(
    EmployeeActivityTypeId.TWO_FACTOR_VERIFIED,
    () => $t('Two-factor verified'),
  )
  static readonly OTP_SET_UP = new EmployeeActivityType(EmployeeActivityTypeId.OTP_SET_UP, () =>
    $t('OTP set up'),
  )
  static readonly PASSWORD_CHANGED = new EmployeeActivityType(
    EmployeeActivityTypeId.PASSWORD_CHANGED,
    () => $t('Password changed'),
  )
  static readonly PASSWORD_CHANGE_REQUESTED = new EmployeeActivityType(
    EmployeeActivityTypeId.PASSWORD_CHANGE_REQUESTED,
    () => $t('Password reset requested'),
  )
  static readonly GRACE_LOGIN = new EmployeeActivityType(EmployeeActivityTypeId.GRACE_LOGIN, () =>
    $t('Logged in using grace login'),
  )
  static readonly GRACE_LOGIN_CREATED = new EmployeeActivityType(
    EmployeeActivityTypeId.GRACE_LOGIN_CREATED,
    () => $t('Grace login created'),
  )
  static readonly GRACE_LOGIN_REVOKED = new EmployeeActivityType(
    EmployeeActivityTypeId.GRACE_LOGIN_REVOKED,
    () => $t('Grace login revoked'),
  )
  static readonly EMPLOYEE_INVITED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_INVITED,
    () => $t('Invitation sent'),
  )
  static readonly INVITATION_ACCEPTED = new EmployeeActivityType(
    EmployeeActivityTypeId.INVITATION_ACCEPTED,
    () => $t('Invitation accepted'),
  )
  static readonly EMPLOYEE_CREATED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_CREATED,
    () => $t('Employee created'),
  )
  static readonly EMPLOYEE_UPDATED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_UPDATED,
    () => $t('Employee details updated'),
  )
  static readonly EMPLOYEE_ACTIVATED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_ACTIVATED,
    () => $t('Employee activated'),
  )
  static readonly EMPLOYEE_DEACTIVATED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_DEACTIVATED,
    () => $t('Employee deactivated'),
  )
  static readonly EMPLOYEE_ROLES_CHANGED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_ROLES_CHANGED,
    () => $t('Roles changed'),
  )
  static readonly EMPLOYEE_COUNTRIES_CHANGED = new EmployeeActivityType(
    EmployeeActivityTypeId.EMPLOYEE_COUNTRIES_CHANGED,
    () => $t('Countries changed'),
  )
  static readonly API_USER_CREATED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_USER_CREATED,
    () => $t('API user created'),
  )
  static readonly API_USER_UPDATED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_USER_UPDATED,
    () => $t('API user updated'),
  )
  static readonly API_USER_ACTIVATED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_USER_ACTIVATED,
    () => $t('API user activated'),
  )
  static readonly API_USER_DEACTIVATED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_USER_DEACTIVATED,
    () => $t('API user deactivated'),
  )
  static readonly API_KEY_GENERATED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_KEY_GENERATED,
    () => $t('API key generated'),
  )
  static readonly API_KEY_REVOKED = new EmployeeActivityType(
    EmployeeActivityTypeId.API_KEY_REVOKED,
    () => $t('API key revoked'),
  )

  private constructor(
    public readonly id: EmployeeActivityTypeId,
    public readonly name: () => string,
  ) {
    super(id)
  }
}
